import { useRouter } from 'next/router';
import { useCallback, useState, useEffect } from 'react';
import useInputChange from '@replacements/hooks/useInputChange';
import {
  formatInvalidCharsForMessage,
  hasInputDoubleColons,
  isInputValidRespelling,
} from '@replacements/utils/respellingUtil';
import ErrorCodes from '@replacements/utils/errorCodes';
import useAddPhoneticReplacement from '@replacements/core/useAddPhoneticReplacement';
import useUpdatePhoneticReplacement from '@replacements/hooks/replacementsLibrary/useUpdatePhoneticReplacement';
import { ReplacementLibraryItem } from '@replacements/components/ReplacementsLibrary/Accordion/ReplacementsLibraryAccordion';
import { useReplacementsLibraryContext } from '@replacements/context/ReplacementsLibraryContext';
import { useEventsContext } from '../../../context/events/EventsContext';
import useSelectedPreviewActor from '../../avatar/hooks/useSelectedPreviewActor';
import {
  PhoneticLibraryType,
  PhoneticReplacement,
} from '../../../../server/graphql/__generated.types';

export const MAX_PHONETIC_REPLACEMENT_LENGTH = 64;

interface UsePhoneticOptions {
  onSuccess: (phoneticReplacement: PhoneticReplacement) => void;
  onSuccessUpdate?: (phoneticReplacement: PhoneticReplacement) => void;
  defaultPreviewWord?: string;
}

const usePhonetic = (options: UsePhoneticOptions) => {
  const { onSuccess, onSuccessUpdate, defaultPreviewWord = '' } = options;
  const router = useRouter();
  const path = router?.route!;
  const [isRespellingState, setIsRespelling] = useState(false);
  const [errorState, setErrorState] = useState<Error | null>(null);
  const onInputChange = () => {
    setErrorState(null);
  };
  const preview = useInputChange(defaultPreviewWord, onInputChange);
  const replacement = useInputChange('', onInputChange);
  const { setDefaultReplacementOriginalWord } = useReplacementsLibraryContext();

  useEffect(() => {
    /**
     * If user is creating replacement via toolbar in editor, pre-fill user-highlighted word
     */
    if (defaultPreviewWord) {
      preview.onTextChange(defaultPreviewWord);
      setDefaultReplacementOriginalWord('');
    }
  }, [
    defaultPreviewWord,
    preview,
    preview.text,
    setDefaultReplacementOriginalWord,
  ]);

  const { clearSelectedPreviewActor } = useSelectedPreviewActor();
  const [eventTracking] = useEventsContext();

  const resetForm = useCallback(() => {
    preview.reset();
    replacement.reset();
    setErrorState(null);
  }, [preview, replacement]);

  const handleComplete = (value: {
    phoneticReplacement: PhoneticReplacement;
  }) => {
    resetForm();
    const { libraryId } = value.phoneticReplacement;
    eventTracking.trackPhoneticLibraryCreation({
      path,
      libraryType: libraryId ? 'team' : 'personal',
    });
    onSuccess(value.phoneticReplacement);
  };

  const handleError = (error: Error) => {
    setErrorState(error);
  };

  const [addPhoneticReplacement, { loading }] = useAddPhoneticReplacement({
    // normalize onCompleted to mirror legacy
    onCompleted: v => {
      if (v.phoneticReplacementCreate.replacement) {
        handleComplete({
          phoneticReplacement: v.phoneticReplacementCreate.replacement,
        });
      }
    },
    onError: handleError,
  });

  // Mutation for updating a replacement
  const [updatePhoneticReplacement] = useUpdatePhoneticReplacement({
    // normalize onCompleted to mirror legacy
    onCompleted: d => {
      if (onSuccessUpdate) {
        if (d.phoneticReplacementUpdate.replacement) {
          onSuccessUpdate(d.phoneticReplacementUpdate.replacement);
        }
      }
    },
  });

  const validateInputs = useCallback(
    (currentReplacement?: ReplacementLibraryItem): boolean => {
      if (isRespellingState) {
        if (hasInputDoubleColons(replacement.text)) {
          setErrorState(new Error(ErrorCodes.double_colon_respelling.message));
          return false;
        }
        const respellingValid = isInputValidRespelling(replacement.text);
        if (!respellingValid) {
          setErrorState(
            new Error(
              `Failed to save. Respelling cannot contain: ${formatInvalidCharsForMessage(
                replacement.text
              )}.`
            )
          );
          return false;
        }
      }
      if (replacement.text.length > MAX_PHONETIC_REPLACEMENT_LENGTH) {
        setErrorState(new Error(ErrorCodes.replacement_too_long.message));
        return false;
      }
      if (preview.text.length > MAX_PHONETIC_REPLACEMENT_LENGTH) {
        setErrorState(new Error(ErrorCodes.word_too_long.message));
        return false;
      }
      if (
        currentReplacement &&
        replacement.text === currentReplacement.replacement &&
        isRespellingState === currentReplacement.isRespelling
      ) {
        // same replacement entered; do nothing
        return false;
      }
      return true;
    },
    [isRespellingState, preview.text.length, replacement.text]
  );

  // Submit callback
  const onSubmit = useCallback(
    (event: any, libraryType?: PhoneticLibraryType, projectId?: string) => {
      event.preventDefault();
      if (!preview.text || !replacement.text) return;
      if (!validateInputs()) {
        return;
      }
      addPhoneticReplacement({
        variables: {
          inputs: {
            text: preview.text,
            replacement: replacement.text,
            isRespelling: isRespellingState,
            libraryType: libraryType || PhoneticLibraryType.Personal,
            projectId,
          },
        },
      }).catch(() => {
        // ignoring error since we are handling it declaritively (catching to prevent propogation
        // to the error boundary)
      });
    },
    [
      preview.text,
      replacement.text,
      validateInputs,
      addPhoneticReplacement,
      isRespellingState,
    ]
  );

  const onSubmitUpdate = useCallback(
    (
      event: React.FormEvent<HTMLFormElement>,
      currentReplacement: ReplacementLibraryItem
    ) => {
      event.preventDefault();
      if (!validateInputs(currentReplacement)) {
        return;
      }
      updatePhoneticReplacement({
        variables: {
          inputs: {
            id: currentReplacement.id,
            text: preview.text,
            replacement: replacement.text,
            isRespelling: isRespellingState,
          },
        },
      });
    },
    [
      isRespellingState,
      preview.text,
      replacement.text,
      updatePhoneticReplacement,
      validateInputs,
    ]
  );

  /**
   * reset the form on unmount - (avoid persisting active actor state)
   * note: it may be useful in the future to retain text - but invalidating
   * the active actor only in the future
   */
  useEffect(() => {
    clearSelectedPreviewActor();
  }, [path, clearSelectedPreviewActor]);

  return {
    error: errorState,
    resetError: () => {
      setErrorState(null);
    },
    loading,
    onSubmit,
    onSubmitUpdate,
    preview,
    replacement,
    isRespelling: isRespellingState,
    setIsRespelling,
  };
};

export default usePhonetic;
